import {
  DEFAULT_ANIMATION_LAYER,
  deriveAnimationId,
  isDerivableAnimationState,
} from "../../helpers/AnimationIdHelper";
import { InteractionType } from "../../models/interactions/Interaction";

/**
 * Lazy content migration for animation references.
 *
 * Trainings store an animation as its animator state (plus a display name and
 * a layer) because the runtime plays by state name. The API now addresses
 * animations by `externalId`, a GUID derived deterministically from
 * `(state, layer)`. This walker adds that id as `animationId` to every
 * reference that lacks one, so the editor can look the animation up without
 * an API call and without changing what the runtime consumes.
 *
 * Three reference shapes exist in content:
 *  - AnimateActor: `animation: { name, animationState } | string`, with `layer`
 *    as a sibling on the interaction.
 *  - MessageBubble / EmotionBubble (also inside Conversation messages):
 *    `animation: { name, animationState, layer } | string`.
 *  - Actor state (`take.actors[]`): `animation: { id, stateName, layer }`.
 *
 * Rules:
 *  - Pure, synchronous, copy-on-write and identity-preserving: the same
 *    reference is returned when nothing changes, so callers can detect a
 *    migration via `!==`. Never mutates its input (it runs on frozen Redux
 *    payloads inside `setTakes`).
 *  - A missing `layer` defaults to {@link DEFAULT_ANIMATION_LAYER} (0) and is
 *    written back so content and id agree. A different default would derive
 *    a different id.
 *  - Trim only, case preserved (the derivation itself trims). A state that
 *    differs in case or whitespace from what Unity registered derives an id
 *    the API does not know; the editor shows it as "animation unavailable".
 *  - Empty and "none" states get no id.
 *  - An existing `animationId` is never overwritten.
 *  - It runs for every training load, Unity re-import and take copy (via
 *    `normalizeTake3D`) and for clipboard paste (via `readClipboardEnvelope`),
 *    always before the document baseline is taken, so it never marks a
 *    training as modified.
 */
export function ensureAnimationIds<T>(value: T): T {
  return walk(value, new Map()) as T;
}

type AnyRecord = Record<string, unknown>;

function isRecord(value: unknown): value is AnyRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasAnimationId(animation: AnyRecord): boolean {
  return (
    typeof animation.animationId === "string" && animation.animationId.length > 0
  );
}

/** Content may carry the layer as a number or a numeric string; default 0. */
export function resolveAnimationLayer(layer: unknown): number {
  if (typeof layer === "number" && Number.isFinite(layer)) return layer;
  if (typeof layer === "string" && layer.trim() !== "") {
    const parsed = Number(layer);
    if (Number.isFinite(parsed)) return parsed;
  }
  return DEFAULT_ANIMATION_LAYER;
}

function walk(value: unknown, cache: Map<object, unknown>): unknown {
  if (!value || typeof value !== "object") return value;

  // Shared references (a copied interaction shares its animation object with
  // the source) migrate once; the cache also breaks any accidental cycle.
  const cached = cache.get(value);
  if (cached !== undefined) return cached;
  cache.set(value, value);

  let result: unknown;
  if (Array.isArray(value)) {
    result = walkArray(value, cache);
  } else {
    result = migrateRecord(walkRecord(value as AnyRecord, cache));
  }

  cache.set(value, result);
  return result;
}

function walkArray(array: unknown[], cache: Map<object, unknown>): unknown[] {
  let changed = false;
  const next = array.map((item) => {
    const walked = walk(item, cache);
    if (walked !== item) changed = true;
    return walked;
  });
  return changed ? next : array;
}

function walkRecord(record: AnyRecord, cache: Map<object, unknown>): AnyRecord {
  let next: AnyRecord | null = null;
  for (const key of Object.keys(record)) {
    const child = record[key];
    const walked = walk(child, cache);
    if (walked === child) continue;
    if (!next) next = { ...record };
    next[key] = walked;
  }
  return next ?? record;
}

function migrateRecord(record: AnyRecord): AnyRecord {
  switch (record.type) {
    case InteractionType.AnimateActor:
      return migrateAnimateActor(record);
    case InteractionType.MessageBubble:
    case InteractionType.EmotionBubble:
      return migrateBubble(record);
    default:
      return isActorState(record) ? migrateActorState(record) : record;
  }
}

function migrateAnimateActor(record: AnyRecord): AnyRecord {
  const { animation } = record;
  const layer = resolveAnimationLayer(record.layer);

  if (typeof animation === "string") {
    // Legacy form: the string is the animator state (the runtime plays by
    // state), so it becomes both the display name and the state.
    if (!isDerivableAnimationState(animation)) return record;
    return {
      ...record,
      layer,
      animation: {
        name: animation,
        animationState: animation,
        animationId: deriveAnimationId(animation, layer),
      },
    };
  }

  if (!isRecord(animation) || hasAnimationId(animation)) return record;
  const state = animation.animationState;
  if (!isDerivableAnimationState(typeof state === "string" ? state : undefined))
    return record;

  return {
    ...record,
    layer,
    animation: {
      ...animation,
      animationId: deriveAnimationId(state as string, layer),
    },
  };
}

function migrateBubble(record: AnyRecord): AnyRecord {
  const { animation } = record;

  if (typeof animation === "string") {
    // Legacy form, same normalisation as normalizeMessageBubbleAnimation:
    // the string is the state, there is no name, and the layer defaults to 0.
    if (!isDerivableAnimationState(animation)) return record;
    return {
      ...record,
      animation: {
        name: "",
        animationState: animation,
        layer: DEFAULT_ANIMATION_LAYER,
        animationId: deriveAnimationId(animation, DEFAULT_ANIMATION_LAYER),
      },
    };
  }

  if (!isRecord(animation) || hasAnimationId(animation)) return record;
  const state = animation.animationState;
  if (!isDerivableAnimationState(typeof state === "string" ? state : undefined))
    return record;
  const layer = resolveAnimationLayer(animation.layer);

  return {
    ...record,
    animation: {
      ...animation,
      layer,
      animationId: deriveAnimationId(state as string, layer),
    },
  };
}

function isActorState(record: AnyRecord): boolean {
  return (
    typeof record.actorId === "string" &&
    isRecord(record.animation) &&
    typeof record.animation.stateName === "string"
  );
}

function migrateActorState(record: AnyRecord): AnyRecord {
  const animation = record.animation as AnyRecord;
  if (hasAnimationId(animation)) return record;
  const state = animation.stateName as string;
  if (!isDerivableAnimationState(state)) return record;
  const layer = resolveAnimationLayer(animation.layer);

  return {
    ...record,
    animation: {
      ...animation,
      layer,
      animationId: deriveAnimationId(state, layer),
    },
  };
}
